import { useCallback, useEffect, useState } from 'react';

import { BugoBrandBrief } from '@shared/api/bugoBrand/bugoBrand.interface';
import { api_ } from '@shared/plugIn/axios';

import { BrandNameResolver, brandNameResolverOf } from './analyticsBrandName';

/**
 * 상조 이름 맵을 세션에 한 번만 받아 두는 훅.
 * 왜 필요한지(= populate를 왜 걷어냈는지)는 `analyticsBrandName.ts` 주석 참고.
 */

export type { BrandNameResolver } from './analyticsBrandName';
export { defaultBrandNameResolver, brandNameResolverOf } from './analyticsBrandName';

//* 이름만 필요하므로 이미지·설정 같은 무거운 필드는 받지 않는다
const BRAND_SELECT = [
  'type',
  'bugoAgency.corpName',
  'bugoAgency.nickName',
  'bugoAgencyList.corpName',
  'bugoAgencyList.nickName',
].join(' ');

const BRAND_URL = `admin/bugo-brand?select=${encodeURIComponent(BRAND_SELECT)}`;

export type BrandNamesStatus = 'loading' | 'ready' | 'error';

const EMPTY_MAP: Map<string, BugoBrandBrief> = new Map();

//* 탭·뷰마다 다시 받을 이유가 없다 — 세션 동안 한 번만 받아 공유한다
let cachedMap: Map<string, BugoBrandBrief> | null = null;
let inflight: Promise<Map<string, BugoBrandBrief>> | null = null;

const loadBrandMap = (): Promise<Map<string, BugoBrandBrief>> => {
  if (cachedMap) return Promise.resolve(cachedMap);
  if (!inflight) {
    inflight = api_
      .get<BugoBrandBrief[]>(BRAND_URL)
      .then(({ data }) => {
        const map = new Map<string, BugoBrandBrief>();
        (Array.isArray(data) ? data : []).forEach((brand) => {
          if (brand?._id) map.set(brand._id, brand);
        });
        cachedMap = map;
        return map;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
};

/** 테스트에서 세션 캐시를 비운다 */
export const resetAnalyticsBrandNames = (): void => {
  cachedMap = null;
  inflight = null;
};

export const useAnalyticsBrandNames = () => {
  const [map, setMap] = useState<Map<string, BugoBrandBrief>>(cachedMap ?? EMPTY_MAP);
  const [status, setStatus] = useState<BrandNamesStatus>(
    cachedMap ? 'ready' : 'loading',
  );

  useEffect(() => {
    if (cachedMap) return;
    let alive = true;
    loadBrandMap()
      .then((loaded) => {
        if (!alive) return;
        setMap(loaded);
        setStatus('ready');
      })
      .catch(() => {
        if (!alive) return;
        setStatus('error');
      });
    return () => {
      alive = false;
    };
  }, []);

  const resolveBrandName = useCallback<BrandNameResolver>(
    (brand, index) => brandNameResolverOf(map)(brand, index),
    [map],
  );

  return {
    status,
    resolveBrandName,
    /**
     * 이름 맵을 못 받았을 때만 true — 이 경우엔 예전처럼 문서마다 populate해서
     * 상조명이 통째로 비어 보이는 것만은 막는다(응답은 무거워진다).
     */
    brandPopulateFallback: status === 'error',
  };
};
